/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { FloorPlanEditor } from '@kaiizen/kai-floor-plan-editor';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  getGroupFlooringAreaValueNames,
  getGroupFloorPlanRoom,
  getGroupUpdatesFromFloorPlanRoom,
  type ScanGroupOptimized,
  type ScanValue,
  withDefaultCeiling,
} from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';

import { flooringAreaValueNamePresenter, groupNamePresenter } from '@/Client/Presenters';
import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import {
  ModalCard,
  ModalCloseButton,
  ModalDivider,
  ModalShell,
  ModalTitle,
} from '@/Client/Widgets';

// ********************************************************************************
// Styles
// ********************************************************************************
const LINK_ROW_STYLE = css`
  label: LINK_ROW_STYLE;
  margin-top: ${LAYOUT2.spacing1p5}px;
`;

const LINK_STYLE = css`
  label: LINK_STYLE;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  ${TYPOGRAPHY2.smallBold}
  color: var(--textGreen);
  text-decoration: underline;
  text-align: left;
`;

const EDITOR_HOST_STYLE = css`
  label: EDITOR_HOST_STYLE;
  flex: 1;
  min-height: 0;
  margin-top: ${LAYOUT2.spacing1p5}px;
  border: 1px solid var(--border);
  overflow: hidden;
`;

// ********************************************************************************
// Modal
// ********************************************************************************
type RoomMeasurementsModalProps = {
  visible: boolean;
  group: ScanGroupOptimized;
  onClose: () => void;
  onOpenProjectMeasurements?: () => void;
  /** Receives the complete `updateGroup` payload for the edited floor plan. */
  onSave: (groupUpdates: {
    roomFloorPlan: FloorPlanRoom;
    values: Record<string, ScanValue>;
  }) => void | PromiseLike<unknown>;
};

export const RoomMeasurementsModal = ({
  visible,
  group,
  onClose,
  onOpenProjectMeasurements,
  onSave,
}: RoomMeasurementsModalProps) => {
  const flooringAreaValueNames = getGroupFlooringAreaValueNames(group.template);
  const initialRoom = useMemo(() => {
    const room = getGroupFloorPlanRoom(group);
    return room && withDefaultCeiling(room);
  }, [group]);
  // When the scan isn't editable for the current user, the floor plan opens
  // view-only: fully selectable and inspectable, but nothing can be changed.
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);

  const handleSave = (room: FloorPlanRoom) => onSave(getGroupUpdatesFromFloorPlanRoom(group, room));

  return (
    <ModalShell visible={visible} onClose={onClose} fullScreen closeOnBackdropClick={false}>
      <ModalCard denseBottomPadding fillHeight>
        <ModalCloseButton onClick={onClose} />
        <ModalTitle>Measurements: {groupNamePresenter(group)}</ModalTitle>
        <div css={LINK_ROW_STYLE}>
          <button
            type='button'
            css={LINK_STYLE}
            onClick={() => {
              onClose();
              onOpenProjectMeasurements?.();
            }}
          >
            See total project measurements &gt;
          </button>
        </div>
        <ModalDivider />
        <div css={EDITOR_HOST_STYLE}>
          <FloorPlanEditor
            key={group.id}
            floorTypes={flooringAreaValueNames.map(name => ({
              key: name,
              label: flooringAreaValueNamePresenter(name),
              id: name,
            }))}
            defaultRoom={initialRoom}
            onSave={scanIsEditable ? handleSave : undefined}
            readOnly={!scanIsEditable}
          />
        </div>
      </ModalCard>
    </ModalShell>
  );
};
